import React, { useContext, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import WitnessPicker from '../../../../components/Medicines/WitnessPicker'
import {
  emptyWitness,
  isWitnessComplete,
  toWitnessPayload,
} from '../../../../helpers/medicineWitness'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '32rem',
  maxWidth: '90vw',
  bgcolor: 'background.paper',
  borderRadius: 1,
  boxShadow: 24,
  padding: 2,
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
}

// Voids an administration that was a pure recording error - nothing was
// actually given. Unlike a correction, this returns the stock to available
// inventory (server-side), so it needs its own justification separate from
// the original record, which stays untouched and visible.
const VoidAdministrationModal = ({ administration, onClose, onVoided }) => {
  const { setToast } = useContext(GlobalContext)
  const [reason, setReason] = useState('')
  const [witness, setWitness] = useState(emptyWitness())
  const [submitting, setSubmitting] = useState(false)

  const isControlled = !!administration?.medicineDescription?.isControlled

  const handleSubmit = (e) => {
    e.preventDefault()
    setSubmitting(true)
    API.post(`/medsmx/administration/${administration._id}/void`, {
      reason,
      witness: isControlled ? toWitnessPayload(witness) : undefined,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Administration voided and stock returned to inventory' })
          onVoided?.(res.data)
          onClose()
        } else {
          setToast({ message: 'Failed to void administration', severity: 'error' })
        }
      })
      .catch((e) => {
        console.error(e)
        setToast({ message: 'Failed to void administration', severity: 'error' })
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Modal open={!!administration} onClose={onClose}>
      <Box sx={style} component="form" onSubmit={handleSubmit}>
        <Typography variant="title2" fontSize="1.2rem" color="anRed">
          Void Administration
        </Typography>
        <Alert severity="error" icon={false}>
          <AlertTitle>This means nothing was actually given</AlertTitle>
          The original record stays permanently on file for audit, marked as
          voided. The stock item(s) used will be returned to available
          inventory in {administration?.medicineLocation?.name ?? 'the bag'}.
          {isControlled &&
            ' This is a controlled drug - a witness is required to void it, same as administering it.'}
        </Alert>
        <TextField
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          label="Reason for voiding"
          required
          autoComplete="off"
          multiline
          minRows={2}
        />
        {isControlled && (
          <WitnessPicker
            value={witness}
            onChange={setWitness}
            size="medium"
            preventSelectingSelf={false}
            excludeUserIds={[administration?.administeredBy?._id]}
          />
        )}
        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={
              submitting ||
              !reason ||
              (isControlled && !isWitnessComplete(witness))
            }
          >
            {submitting ? 'Voiding...' : 'Void Administration'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  )
}

export default VoidAdministrationModal
